import { defineStore } from 'pinia';
import { useUrlSourcesStore } from '@/store/urlSourcesStore';
import IUrl from '@/interfaces/IUrl';
import UrlTypes from '@/enums/UrlTypes';
import UrlFetchStatus from '@/enums/UrlFetchStatus';

export const useUrlsStore = defineStore('UrlsStore', {
  state: () => ({
    urls: [] as IUrl[],
  }),
  getters: {
    getNumberOfCategoryUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.CATEGORY
      ).length;
    },
    getNumberOfProductUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.PRODUCT
      ).length;
    },
    getNumberOfPageUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.PAGE
      ).length;
    },
    getNumberOfOtherUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.OTHER
      ).length;
    },
    getNumberOfCategoryHubUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.CATEGORY_HUB
      ).length;
    },
    getNumberOfUnclassifiedUrls(): number {
      return this.urls.filter(
        (url) => url.urlType === UrlTypes.UNCLASSIFIED
      ).length;
    },
  },
  actions: {

    addUrlObject(url: IUrl) {
      if (url.pathname === '/') {
        return;
      }
      // Only add if not already existing (uniqueness based on pathname)
      if (!this.urls.some((entry) => entry.pathname === url.pathname)) {
        this.urls.push(url);
      }
    },
    markFetchedWithTypeCorrection(pathname: string, correctedType: UrlTypes) {
      const entry = this.urls.find(url => url.pathname === pathname);
      if (!entry) return;
      const original = entry.urlType;
      entry.fetchStatus = UrlFetchStatus.FETCHED;
      entry.urlType = correctedType;
      entry.originalUrlType = original;
    },
    markFetchedAsRedirected(pathname: string) {
      const entry = this.urls.find(url => url.pathname === pathname);
      if (!entry) return;
      entry.fetchStatus = UrlFetchStatus.FETCHED;
      entry.isRedirected = true;
    },
    markFetchFailed(pathname: string) {
      const entry = this.urls.find(url => url.pathname === pathname);
      if (!entry) return;
      entry.fetchStatus = UrlFetchStatus.FAILED;
      // If the URL type is UNCLASSIFIED and the fetch fails, we change its type to OTHER.
      // This ensures that failed unclassified URLs are not treated as unclassified anymore,
      if (entry.urlType === UrlTypes.UNCLASSIFIED) {
        entry.originalUrlType = entry.urlType;
        entry.urlType = UrlTypes.OTHER;
      }
    },
    markFetchSuccessful(pathname: string) {
      const entry = this.urls.find(url => url.pathname === pathname);
      if (!entry) return;
      entry.fetchStatus = UrlFetchStatus.FETCHED;
    },
    markFetchInProgress(pathname: string) {
      const entry = this.urls.find(url => url.pathname === pathname);
      if (!entry) return;
      entry.fetchStatus = UrlFetchStatus.IN_PROGRESS;
    },
    clearUrlSourceStore() {
      this.urls = [];
    },
    getPendingUrlsToFetch(): IUrl[] {
      const urlSources = useUrlSourcesStore();
      return this.urls.filter(
        (u) =>
          u.fetchStatus === UrlFetchStatus.TO_FETCH &&
          !urlSources.hasUrl(u.pathname),
      );
    },
  },
});
